import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { useLanguage } from '../../../contexts/LanguageContext';

const WorkersTable = ({ workers, onViewInduction, onEditWorker }) => {
  const { t } = useLanguage();
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  const statusOptions = [
    { value: 'all', label: t('allWorkers') },
    { value: 'inducted', label: t('inducted') },
    { value: 'pending', label: t('pendingInduction') },
    { value: 'expiring', label: t('certificatesExpiring') }
  ];

  const sortOptions = [
    { value: 'name', label: t('name') },
    { value: 'induction', label: t('inductionDate') },
    { value: 'certificates', label: t('certificateStatus') }
  ];

  const getInductionBadge = (status) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success rounded-md text-xs font-medium">
            <Icon name="CheckCircle2" size={14} />
            {t('completed')}
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning rounded-md text-xs font-medium">
            <Icon name="Clock" size={14} />
            {t('pending')}
          </span>
        );
      default:
        return null;
    }
  };

  const getCertificateStatus = (expiringCount) => {
    if (expiringCount === 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success rounded-md text-xs font-medium">
          <Icon name="CheckCircle2" size={14} />
          {t('allValid')}
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning/10 text-warning rounded-md text-xs font-medium">
          <Icon name="AlertTriangle" size={14} />
          {expiringCount} {t('expiring')}
        </span>
      );
    }
  };

  const filteredWorkers = workers?.filter(worker => {
      if (filterStatus === 'all') return true;
      if (filterStatus === 'inducted') return worker?.inductionStatus === 'completed';
      if (filterStatus === 'pending') return worker?.inductionStatus === 'pending';
      if (filterStatus === 'expiring') return worker?.expiringCertificates > 0;
      return true;
    })?.sort((a, b) => {
      if (sortBy === 'name') {
        return a?.name?.localeCompare(b?.name);
      } else if (sortBy === 'induction') {
        return new Date(b.inductionDate || 0) - new Date(a.inductionDate || 0);
      } else {
        return b?.expiringCertificates - a?.expiringCertificates;
      }
    });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <Select
          options={statusOptions}
          value={filterStatus}
          onChange={setFilterStatus}
          placeholder={t('filterByStatus')}
          className="flex-1"
        />
        <Select
          options={sortOptions}
          value={sortBy}
          onChange={setSortBy}
          placeholder={t('sortBy')}
          className="flex-1"
        />
      </div>
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-muted">
              <th className="text-left p-3 text-sm font-semibold text-foreground">{t('worker')}</th>
              <th className="text-left p-3 text-sm font-semibold text-foreground">{t('role')}</th>
              <th className="text-left p-3 text-sm font-semibold text-foreground">{t('contact')}</th>
              <th className="text-left p-3 text-sm font-semibold text-foreground">{t('inductionStatus')}</th>
              <th className="text-left p-3 text-sm font-semibold text-foreground">{t('inductionDate')}</th>
              <th className="text-left p-3 text-sm font-semibold text-foreground">{t('certificates')}</th>
              <th className="text-right p-3 text-sm font-semibold text-foreground">{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredWorkers?.map((worker) => (
              <tr key={worker?.id} className="border-b border-border hover:bg-muted/50 transition-smooth">
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-accent text-accent-foreground rounded-full font-medium text-sm">
                      {worker?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                    </div>
                    <span className="font-medium text-foreground">{worker?.name}</span>
                  </div>
                </td>
                <td className="p-3 text-sm text-muted-foreground">{worker?.role}</td>
                <td className="p-3">
                  <div className="text-sm">
                    <p className="text-foreground">{worker?.phone}</p>
                    <p className="text-muted-foreground">{worker?.email}</p>
                  </div>
                </td>
                <td className="p-3">{getInductionBadge(worker?.inductionStatus)}</td>
                <td className="p-3 text-sm text-muted-foreground">
                  {worker?.inductionDate || t('notCompleted')}
                </td>
                <td className="p-3">{getCertificateStatus(worker?.expiringCertificates)}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onViewInduction(worker?.id)}
                      aria-label={t('viewInduction')}
                      title={t('viewInduction')}
                    >
                      <Icon name="ClipboardCheck" size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEditWorker(worker?.id)}
                      aria-label={t('edit')}
                      title={t('edit')}
                    >
                      <Icon name="Edit" size={18} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden space-y-3">
        {filteredWorkers?.map((worker) => (
          <div key={worker?.id} className="bg-card border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-12 h-12 bg-accent text-accent-foreground rounded-full font-medium flex-shrink-0">
                {worker?.name?.split(' ')?.map(n => n?.[0])?.join('')}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground mb-1">{worker?.name}</h4>
                <p className="text-sm text-muted-foreground">{worker?.role}</p>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Icon name="Phone" size={16} className="text-muted-foreground" />
                <span className="text-foreground">{worker?.phone}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Mail" size={16} className="text-muted-foreground" />
                <span className="text-foreground">{worker?.email}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
              <div>
                <span className="text-xs text-muted-foreground font-caption">{t('induction')}</span>
                <div className="mt-1">{getInductionBadge(worker?.inductionStatus)}</div>
                {worker?.inductionDate && (
                  <p className="text-xs text-muted-foreground mt-1">{worker?.inductionDate}</p>
                )}
              </div>
              <div>
                <span className="text-xs text-muted-foreground font-caption">{t('certificates')}</span>
                <div className="mt-1">{getCertificateStatus(worker?.expiringCertificates)}</div>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewInduction(worker?.id)}
                iconName="ClipboardCheck"
                iconPosition="left"
                className="flex-1"
              >
                {t('induction')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEditWorker(worker?.id)}
                iconName="Edit"
                iconPosition="left"
                className="flex-1"
              >
                {t('edit')}
              </Button>
            </div>
          </div>
        ))}
      </div>
      {filteredWorkers?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 px-4 bg-muted rounded-lg">
          <Icon name="Users" size={48} className="text-muted-foreground mb-3" />
          <p className="text-muted-foreground text-center">{t('noWorkersMatchFilters')}</p>
        </div>
      )}
    </div>
  );
};

export default WorkersTable;